/**
 * The 44-class OLM leaf catalog used by `<ItemEditList>` / `<ClassPicker>`.
 *
 * Source of truth: `litter-detector-baseline/configs/taxonomies/olm_leaves.json`
 * filtered to leaves the V1 model knows about (the union of OLM leaves
 * TACO categories map to via the crosswalk), PLUS leaves added ahead of
 * model support so contributor tags can exist before the detector does
 * (currently just `smoking.disposable_vape`, issue #103 — vapes are the
 * growth litter item; the model never emits these, so they only enter
 * data as user-added tags). Order is alphabetical by leaf — same as
 * `prepare_dataset.py` produces in `data.yaml`; class identity between
 * training and UI is carried by the wire value, never by array index.
 *
 * Wire format: snake_case `category.object` (matches OLM's API + our
 * trained model weights). UI surfaces should render the prose `label`
 * for human readability — never display the raw wire value.
 *
 * Category groups support the picker's browse-mode (Section 2 req #7 of
 * `docs/item-edit-list-spec.md`).
 */

import type { ClassEntry } from './types';

export const LITTER_CLASSES: ClassEntry[] = [
  { value: 'alcohol.bottle',              category: 'Alcohol',     label: 'Beer / wine bottle' },
  { value: 'alcohol.bottle_cap',          category: 'Alcohol',     label: 'Beer bottle cap' },
  { value: 'alcohol.broken_glass',        category: 'Alcohol',     label: 'Broken glass' },
  { value: 'alcohol.glass',               category: 'Alcohol',     label: 'Drinking glass' },
  { value: 'alcohol.six_pack_rings',      category: 'Alcohol',     label: 'Six-pack rings' },
  { value: 'coffee.cup',                  category: 'Coffee',      label: 'Coffee cup' },
  { value: 'electronics.battery',         category: 'Electronics', label: 'Battery' },
  { value: 'food.bag',                    category: 'Food',        label: 'Paper food bag' },
  { value: 'food.box',                    category: 'Food',        label: 'Cardboard food box' },
  { value: 'food.can',                    category: 'Food',        label: 'Food can' },
  { value: 'food.container',              category: 'Food',        label: 'Plastic food container' },
  { value: 'food.crisp_packet',           category: 'Food',        label: 'Chip / crisp packet' },
  { value: 'food.cutlery',                category: 'Food',        label: 'Plastic cutlery' },
  { value: 'food.jar',                    category: 'Food',        label: 'Glass jar' },
  { value: 'food.lid',                    category: 'Food',        label: 'Food lid' },
  { value: 'food.other',                  category: 'Food',        label: 'Other food waste' },
  { value: 'food.pizza_box',              category: 'Food',        label: 'Pizza box' },
  { value: 'food.takeaway_container',     category: 'Food',        label: 'Takeaway container' },
  { value: 'food.tinfoil',                category: 'Food',        label: 'Tin foil' },
  { value: 'food.wrapper',                category: 'Food',        label: 'Food wrapper' },
  { value: 'marine.polystyrene_fragment', category: 'Marine',      label: 'Styrofoam piece', keywords: 'foam polystyrene packing' },
  { value: 'marine.rope',                 category: 'Marine',      label: 'Rope' },
  { value: 'medical.gloves',              category: 'Medical',     label: 'Disposable glove' },
  { value: 'medical.pill_pack',           category: 'Medical',     label: 'Pill blister pack' },
  { value: 'other.bags_litter',           category: 'Other',       label: 'Bag of litter' },
  { value: 'other.clothing',              category: 'Other',       label: 'Clothing' },
  { value: 'other.metal',                 category: 'Other',       label: 'Scrap metal' },
  { value: 'other.other',                 category: 'Other',       label: 'Other (uncategorized)' },
  { value: 'other.paper',                 category: 'Other',       label: 'Paper' },
  { value: 'other.plastic',               category: 'Other',       label: 'Plastic piece' },
  { value: 'other.plastic_bag',           category: 'Other',       label: 'Plastic bag', keywords: 'grocery shopping carrier' },
  { value: 'sanitary.deodorant_can',      category: 'Sanitary',    label: 'Aerosol / deodorant can' },
  { value: 'sanitary.tissue',             category: 'Sanitary',    label: 'Tissue' },
  { value: 'sanitary.toothpaste_tube',    category: 'Sanitary',    label: 'Toothpaste tube' },
  { value: 'smoking.butts',               category: 'Smoking',     label: 'Cigarette butt' },
  { value: 'smoking.disposable_vape',     category: 'Smoking',     label: 'Disposable vape' },
  { value: 'softdrinks.bottle',           category: 'Soft drinks', label: 'Plastic soda bottle', keywords: 'water juice sports drink pet' },
  { value: 'softdrinks.bottle_cap',       category: 'Soft drinks', label: 'Plastic bottle cap' },
  { value: 'softdrinks.can',              category: 'Soft drinks', label: 'Soda can' },
  { value: 'softdrinks.carton',           category: 'Soft drinks', label: 'Drink carton' },
  { value: 'softdrinks.cup',              category: 'Soft drinks', label: 'Plastic drinking cup', keywords: 'solo party' },
  { value: 'softdrinks.lid',              category: 'Soft drinks', label: 'Plastic cup lid' },
  { value: 'softdrinks.pull_ring',        category: 'Soft drinks', label: 'Soda can pull tab' },
  { value: 'softdrinks.straw',            category: 'Soft drinks', label: 'Drinking straw' },
];

/** Lookup table value → human label. */
export const CLASS_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  LITTER_CLASSES.map((c) => [c.value, c.label]),
);

/** Lookup table value → category. */
export const CLASS_CATEGORY_BY_VALUE: Record<string, string> = Object.fromEntries(
  LITTER_CLASSES.map((c) => [c.value, c.category]),
);

/**
 * Returns prose label for an OLM leaf, or the raw value with dots
 * replaced by spaces as a fallback for unknown classes (e.g. if a model
 * version-mismatch surfaces a leaf this UI doesn't know about).
 */
export function classLabel(value: string): string {
  const known = CLASS_LABEL_BY_VALUE[value];
  if (known) return known;
  // Contributor-coined tag (ClassPicker's "Use <what I typed>"). Render
  // the words back, not the wire slug — `custom.plastic_water_bottle`
  // reads as "plastic water bottle", never "custom / plastic_water_bottle".
  if (value.startsWith('custom.')) {
    return value.slice('custom.'.length).replace(/_/g, ' ');
  }
  return value.replace(/\./g, ' / ');
}

// ─── Safety-relevant handling flags ────────────────────────────────────────
//
// Class-level analog of the marking the ingest pipeline already gives
// hazard items (scripts/lib/olm-profiles.mjs maps sharps/sanitary/biohazard
// OLM objects onto the 'hazards' display profile). Attached here as a
// lookup table — same pattern as CLASS_LABEL_BY_VALUE — rather than a
// field on ClassEntry, so the backend-mirrored wire contract in types.ts
// stays untouched.

/** Wire-stable id for WHY a class needs care during pickup. */
export type HandlingRisk = 'sharp' | 'battery_fire' | 'biohazard' | 'pressurized';

export interface ClassHandling {
  risk: HandlingRisk;
  /**
   * Handling copy surfaced wherever the class is tagged. Constraint the
   * code can't express: copy is about the OBJECT — what it is and why it
   * needs care — never about whoever dropped it or is picking it up.
   */
  note: string;
}

export const CLASS_HANDLING_BY_VALUE: Record<string, ClassHandling> = {
  'alcohol.broken_glass': {
    risk: 'sharp',
    note: 'Broken glass cuts through bags and gloves — collect it in a rigid container.',
  },
  'electronics.battery': {
    risk: 'battery_fire',
    note: 'Batteries are e-waste — crushed cells can short and start fires, so keep them out of bagged trash. Battery drop-off points take them.',
  },
  'medical.gloves': {
    risk: 'biohazard',
    note: 'Used gloves can carry whatever they last touched — pick them up with a grabber or a bag over the hand.',
  },
  'sanitary.deodorant_can': {
    risk: 'pressurized',
    note: 'Aerosol cans hold pressure even when empty — do not crush or puncture them.',
  },
  'smoking.disposable_vape': {
    risk: 'battery_fire',
    note: 'Vapes are e-waste — a lithium battery is sealed inside, and crushed batteries start fires in trucks and bins. Keep vapes separate from bagged trash; battery drop-off points take them.',
  },
};

/** Handling flag for an OLM leaf, or undefined when the class needs none. */
export function classHandling(value: string): ClassHandling | undefined {
  return CLASS_HANDLING_BY_VALUE[value];
}
